/**
 * Editor dashboard (step 1a, design 2026-10-01).
 *
 * Self-contained and brand-independent: an editor is a person on no brand, so
 * this screen does NOT use BrandContext. It probes `/api/editor/me` itself and
 * renders one of five states - loading, signed-out, not-an-editor, dashboard,
 * or a single focused card (the `/editor/card/:id` deep link, spec #93).
 *
 * Reached three ways:
 *   - `/editor` directly (the invite link's target).
 *   - the app shell renders it for a signed-in person with no brand
 *     (`status === "membership-required"`), which every editor is.
 *   - `/editor/card/:id`, a shareable deep link to one card (spec #93).
 *
 * @module web/screens/EditorScreen
 */
import { GiveBackConfirm } from "./GiveBackConfirm.tsx";
import { DriveHandin } from "./DriveHandin.tsx";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError, post, raw } from "../api/client.ts";
import type { EditorCard, EditorEarnings, EditorWork } from "../api/types.ts";
import { I18nProvider, localeFromLanguages, usePseudo, useI18n, type I18n, type MessageKey } from "../i18n/index.tsx";
import { type Vars } from "../i18n/core.ts";
import type { EditorProfile } from "./editorProfile.ts";
import { cardLanguage, graceMinutesLeft, initials, relative } from "./editorFormat.ts";
import { ClientFeedback } from "./ClientFeedback.tsx";
import { editorCardHref, HubDashboard, HubPage, HubPageHead, ProfileLocation, type HubNav } from "./EditorHub.tsx";
import { CardDetailsPage } from "./EditorCardDetails.tsx";

/**
 * "€68.00 · approx. 60s" / "€30.00 · Static" (spec #299) - this editor's own
 * price preview (already resolved per their own stage by the server) with
 * the card's length riding along, so a cheaper card visibly reads as a
 * shorter one rather than a silent pay cut. Null when the card somehow has
 * no rate at all (should never reach an offer).
 */
function earnText(card: EditorCard, { t, eur }: I18n): string | null {
  if (card.payoutPreviewCents == null) return null;
  const amount = eur(card.payoutPreviewCents);
  if (card.rateType === "static") return t("dashboard.earnStatic", { amount });
  if (card.estimatedSeconds != null) return t("dashboard.earnApprox", { amount, seconds: card.estimatedSeconds });
  return amount;
}
import { EditorBilling } from "./EditorBilling.tsx";
import { EditorBillingDetailsScreen, BillingDetailsPrompt } from "./BillingDetails.tsx";
import { SetupDropdown } from "./EditorOnboarding.tsx";
import { EditorHandbookScreen } from "./EditorHandbook.tsx";
import "../editor.css";

/**
 * The Hub retires (spec #302, 2026-10-30) and hub.aditor.ai redirects here -
 * the Handbook is a portal page (`EditorHandbookScreen`) instead of a link out,
 * and the Adlingo/Mentorship tiles below are live data instead of link-outs
 * (decision Shawn 2026-10-08, "core now, widgets as links" - now completed).
 * Billing migrated in natively earlier (spec #154).
 */
const HANDIN_URL = "https://feedback.aditor.ai/handin";

type View =
  | { kind: "loading" }
  | { kind: "signed-out" }
  | { kind: "not-editor" }
  | { kind: "error"; message: MessageKey; vars?: Vars }
  | { kind: "ready"; me: EditorProfile; work: EditorWork; earnings: EditorEarnings }
  | { kind: "card"; me: EditorProfile; card: EditorCard | null };

/**
 * @param notEditor What a signed-in non-editor sees instead of the editor gate.
 *   The client shell passes its no-brand screen: a client must never be told
 *   they lack "editor access". Only `/editor` itself shows the gate.
 * @param cardId The per-card deep link target (spec #93): `/editor/card/:id`.
 *   When set, the dashboard's whole-draw fetch is skipped in favour of
 *   `GET /api/editor/work/:id` - the one card this link points at, or null if
 *   it is not available to this editor.
 * @param trelloShortLink `/editor/card/t/:shortLink`, the link a comment ping
 *   sends: resolved to this editor's card and replaced by its own URL, or the
 *   same "not available" page as an unknown card id.
 */
export function EditorScreen({ notEditor, cardId, trelloShortLink }: { notEditor?: ReactNode; cardId?: string; trelloShortLink?: string } = {}) {
  const pseudo = usePseudo();
  const [view, setView] = useState<View>({ kind: "loading" });
  // The decline/give-back pause+trust confirmation (spec #291): lifted up here
  // rather than kept local to the acting tile, because a decline empties the
  // offer slot on reload and swaps in a different component (OfferCard ->
  // EmptyOfferCard) - local state in the unmounted tile would be lost before
  // the editor ever reads it. Auto-dismisses; a later action's note replaces
  // it immediately.
  const [actionNote, setActionNote] = useState<string | null>(null);
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => { liveRef.current = false; };
  }, []);
  useEffect(() => {
    if (!actionNote) return;
    const t = setTimeout(() => setActionNote(null), 8000);
    return () => clearTimeout(t);
  }, [actionNote]);

  // Honoured by `safeReturnTo` in oidc-routes.ts (anything starting with
  // `/editor`), so a signed-out hit on the deep link comes right back to it
  // after central auth instead of landing on the plain dashboard.
  const returnTo = trelloShortLink ? `/editor/card/t/${trelloShortLink}` : cardId ? `/editor/card/${cardId}` : "/editor";

  /**
   * @param afterAction True when this call is a refresh right after the
   *   editor's OWN mutation on this same screen (accept/decline/submit/
   *   give-back), not a fresh navigation. Passed through as `?afterAction=1`
   *   on the per-card fetch so the server never counts that reload as the
   *   editor "opening" the briefing (spec #186) - only a genuine new visit
   *   (mount) does.
   */
  const load = useCallback(async (opts?: { afterAction?: boolean }) => {
    try {
      const me = await raw<EditorProfile>("/api/editor/me");
      if (!liveRef.current) return;
      if (me.status === 401) {
        // Signed out: go straight to central auth instead of showing a sign-in
        // screen first. The one exception is a just-failed attempt
        // (?signin=failed): show the retry screen so we never bounce in a loop.
        const failed = new URLSearchParams(window.location.search).get("signin") === "failed";
        if (!failed) { window.location.assign(`/api/auth/oidc/login?returnTo=${returnTo}`); return; }
        return setView({ kind: "signed-out" });
      }
      if (me.status === 403) return setView({ kind: "not-editor" });
      if (me.status !== 200 || !me.body) return setView({ kind: "error", message: "dashboard.errorDashboard", vars: { status: me.status } });

      if (trelloShortLink) {
        const found = await raw<{ id: string }>(`/api/editor/work-by-trello/${encodeURIComponent(trelloShortLink)}`);
        if (!liveRef.current) return;
        if (found.status === 200 && found.body) {
          window.location.replace(editorCardHref(found.body.id, window.location.pathname));
          return;
        }
        if (found.status === 404) return setView({ kind: "card", me: me.body, card: null });
        return setView({ kind: "error", message: "dashboard.cardLookupFailed" });
      }

      if (cardId) {
        const suffix = opts?.afterAction ? "?afterAction=1" : "";
        const card = await raw<EditorCard>(`/api/editor/work/${cardId}${suffix}`);
        if (!liveRef.current) return;
        setView({ kind: "card", me: me.body, card: card.status === 200 ? card.body : null });
        return;
      }

      const [work, earnings] = await Promise.all([
        raw<EditorWork>("/api/editor/work"),
        raw<EditorEarnings>("/api/editor/earnings"),
      ]);
      if (!liveRef.current) return;
      if (work.status !== 200 || !work.body) return setView({ kind: "error", message: "dashboard.errorCards" });
      if (earnings.status !== 200 || !earnings.body) return setView({ kind: "error", message: "dashboard.errorEarnings" });
      setView({ kind: "ready", me: me.body, work: work.body, earnings: earnings.body });
    } catch {
      if (liveRef.current) setView({ kind: "error", message: "common.networkError" });
    }
  }, [cardId, trelloShortLink, returnTo]);

  useEffect(() => { void load(); }, [load]);

  // Before `/api/editor/me` answers, the editor's language is unknown: these
  // screens are English, but still go through `t` (and honour the test-only
  // pseudo mode) so they are translatable and walkable.
  if (view.kind === "not-editor" && notEditor) return notEditor;
  if (view.kind === "loading" || view.kind === "signed-out" || view.kind === "not-editor" || view.kind === "error") {
    return (
      <I18nProvider locale="en" pseudo={pseudo}>
        {view.kind === "loading" ? <LoadingScreen />
          : view.kind === "signed-out" ? <SignedOutGate returnTo={returnTo} />
          : view.kind === "not-editor" ? <NotEditorGate />
          : <ErrorScreen message={view.message} vars={view.vars} />}
      </I18nProvider>
    );
  }
  const reload = (note?: string) => {
    if (note) setActionNote(note);
    load({ afterAction: true });
  };

  // Spec #304: everything rendered for this editor - the dashboard and every
  // screen it opens (billing, availability, onboarding, guide, rules) - is in
  // their own language, picked from the company DB's `people.language`.
  return (
    <I18nProvider locale={localeFromLanguages(view.me.languages)} pseudo={pseudo}>
      {view.kind === "card"
        ? <CardFocusView me={view.me} card={view.card} onActed={reload} actionNote={actionNote} />
        : <Dashboard me={view.me} work={view.work} earnings={view.earnings} onActed={reload} actionNote={actionNote} />}
    </I18nProvider>
  );
}

function LoadingScreen() {
  const { t } = useI18n();
  return <div className="editor-shell" data-screen="editor-loading"><p className="editor-sub">{t("dashboard.loadingDashboard")}</p></div>;
}

function ErrorScreen({ message, vars }: { message: MessageKey; vars?: Vars | undefined }) {
  const { t } = useI18n();
  return (
    <div className="editor-shell" data-screen="editor-error">
      <div className="editor-gate"><h1>{t("common.somethingWrong")}</h1><p>{t(message, vars)}</p></div>
    </div>
  );
}

/** `onActed(note?)`: every mutating tile reloads the dashboard the same way, optionally handing up a one-line confirmation (spec #291: decline/give-back's pause+trust summary). */
type OnActed = (note?: string) => void;

function ActionNote({ text }: { text: string | null }) {
  if (!text) return null;
  return <p className="ed-action-note" role="status">{text}</p>;
}

/**
 * Spec #303: unmissable while an admin is in a "view as" session - "Viewing
 * as <editor's first name> - admin", so whoever is looking never mistakes
 * this for their own dashboard. Every write here already answers 403
 * server-side; this banner just says so and when the session ends.
 */
function ViewAsBanner({ me, viewAs }: { me: EditorProfile; viewAs: EditorProfile["viewAs"] }) {
  const { t } = useI18n();
  if (!viewAs) return null;
  const first = me.displayName.split(" ")[0] || me.displayName;
  return (
    <div className="ed-action-note" role="status" data-view-as-banner style={{ background: "#FF6B35", color: "#0B0B0D", fontWeight: 600 }}>
      {t("dashboard.viewAsBanner", { name: first, actor: viewAs.adminActor, when: relative(viewAs.expiresAt, t) })}
    </div>
  );
}

/** An expired pausedUntil is not a pause: the backend (isEligible) only skips dispatch while pausedUntil is still in the future. */
function isPaused(me: EditorProfile): boolean {
  return !!me.pausedUntil && new Date(me.pausedUntil).getTime() > Date.now();
}

/**
 * Why no new offers are coming right now (spec #296 acceptance: "the
 * dashboard says why"), in priority order - a dispatch pause is the most
 * specific and temporary of the three, so it wins over the roster-level
 * reasons even though both could technically be true at once.
 */
function offersPausedReason(me: EditorProfile, t: I18n["t"]): string | null {
  if (isPaused(me)) return t("dashboard.reasonPaused");
  // Spec #302: a Newbie under the 80% Adlingo threshold is never offered a
  // card - the most specific and actionable reason, so it wins over the
  // broader roster-status copy below even though both could be true at once.
  if (me.adlingo?.gateApplies && !me.adlingo.gateMet) {
    return t("dashboard.reasonAdlingo", { threshold: me.adlingo.thresholdPercent, percent: me.adlingo.percent });
  }
  if (me.rosterStatus === "onboarding") return t("dashboard.reasonOnboarding");
  if (me.rosterStatus === "paused") return t("dashboard.reasonRosterPaused");
  return null;
}

type EditorPage = "dashboard" | "billing" | "profile" | "billingDetails" | "handbook";

/** `/editor#billing` - the link every payroll message carries (`notify-format.ts#editorBillingUrl`) - opens Billing directly; anything else opens the dashboard. */
export function editorPageFromHash(hash: string | null | undefined): "dashboard" | "billing" {
  return (hash ?? "").replace(/^#/, "").trim().toLowerCase() === "billing" ? "billing" : "dashboard";
}

async function signOut() {
  try { await fetch("/api/auth/logout", { method: "POST" }); } catch { /* clear cookie best-effort */ }
  window.location.assign("/editor");
}

function Dashboard({ me, work, earnings, onActed, actionNote }: { me: EditorProfile; work: EditorWork; earnings: EditorEarnings; onActed: OnActed; actionNote: string | null }) {
  const [page, setPage] = useState<EditorPage>(() => editorPageFromHash(window.location?.hash));
  // Billing details is a sub-setting of Profile: opened from there, back returns there; opened from the dashboard prompt or the setup list, back returns to the dashboard.
  const [detailsFrom, setDetailsFrom] = useState<EditorPage>("dashboard");
  const { t } = useI18n();

  const openBillingDetails = (from: EditorPage) => { setDetailsFrom(from); setPage("billingDetails"); };
  const home = () => setPage("dashboard");
  // Every page shares the dashboard's header (Shawn 2026-11-01): same greeting, Setup pill, menu and background.
  const nav: HubNav = {
    onBilling: () => setPage("billing"),
    onProfile: () => setPage("profile"),
    onHandbook: () => setPage("handbook"),
    onHome: page === "dashboard" ? undefined : home,
    signOut,
  };
  const setup = <SetupDropdown onOpenBillingDetails={() => openBillingDetails(page)} onOpenHandbook={() => setPage("handbook")} />;
  const viewAs = <ViewAsBanner me={me} viewAs={me.viewAs} />;

  if (page === "billing") return <HubPage me={me} nav={nav} setup={setup} banners={viewAs}><EditorBilling me={me} onBack={home} /></HubPage>;
  if (page === "billingDetails") {
    return <HubPage me={me} nav={nav} setup={setup} banners={viewAs}><EditorBillingDetailsScreen onBack={() => setPage(detailsFrom)} backLabel={detailsFrom === "profile" ? t("dashboard.profileTitle") : detailsFrom === "billing" ? t("billing.title") : t("billing.back")} /></HubPage>;
  }
  if (page === "profile") {
    return <HubPage me={me} nav={nav} setup={setup} banners={viewAs}><EditorProfileScreen me={me} onBack={home} onBillingDetails={() => openBillingDetails("profile")} /></HubPage>;
  }
  if (page === "handbook") return <HubPage me={me} nav={nav} setup={setup} banners={viewAs}><EditorHandbookScreen onBack={home} /></HubPage>;

  return (
    <HubDashboard
      me={me}
      emptyReason={offersPausedReason(me, t)}
      work={work}
      earnings={earnings}
      onActed={onActed}
      nav={nav}
      setup={setup}
      banners={<>
        {viewAs}
        <ActionNote text={actionNote} />
        <BillingDetailsPrompt onOpen={() => openBillingDetails("dashboard")} />
      </>}
    />
  );
}

/**
 * The per-card deep link target (spec #93): the dashboard shell, focused on
 * exactly one card instead of the whole draw. Spec #316: an accepted card
 * (`in_production`/`changes_requested`) renders the full card details page
 * instead of a tile - everything the editor needs, no Trello link. Every
 * other stage keeps its existing tile (`CardFocusTile`): the offer while
 * still offered, read-only history once handed in, a graceful "not
 * available" state for an unknown or not-theirs id.
 */
function CardFocusView({ me, card, onActed, actionNote }: { me: EditorProfile; card: EditorCard | null; onActed: OnActed; actionNote: string | null }) {
  const showDetailsPage = !!card && !card.heldByLabel && !card.clientAssignedWaiting && (card.stage === "in_production" || card.stage === "changes_requested");
  return (
    <HubPage me={me} nav={{ onHome: () => window.location.assign("/editor"), signOut }} banners={<><ViewAsBanner me={me} viewAs={me.viewAs} /><ActionNote text={actionNote} /></>}>
      {showDetailsPage ? (
        <CardDetailsPage workItemId={card!.id} onActed={onActed} />
      ) : (
        <div className="ed-dash hd-page" data-screen="editor-card-focus">
          <div className="ed-stats">
            {card ? <CardFocusTile card={card} me={me} onActed={onActed} /> : <CardUnavailable />}
          </div>
          {card && card.stage !== "offered" && (card.heldByLabel || card.assignedEditorId === me.id) && <ClientFeedback workItemId={card.id} />}
        </div>
      )}
    </HubPage>
  );
}

/** A card the client put on this editor, nothing to act on from this page yet: what the ping's link opens. */
function ClientAssignedTile({ card }: { card: EditorCard }) {
  const { t } = useI18n();
  return (
    <div className="ed-card ed-submit" data-client-assigned-card>
      <h3 className="ed-title">{card.title}</h3>
      <p className="ed-muted" style={{ marginTop: 6 }}>{card.brandName}</p>
      <p className="ed-muted" style={{ marginTop: 6 }}>{t("fixedWeek.clientAssignedPage")}</p>
      <a className="ed-secondary" href="/editor" style={{ display: "inline-block", marginTop: 12 }}>{t("fixedWeek.backToDashboard")}</a>
    </div>
  );
}

function CardFocusTile({ card, me, onActed }: { card: EditorCard; me: EditorProfile; onActed: OnActed }) {
  if (card.clientAssignedWaiting) return <ClientAssignedTile card={card} />;
  if (card.stage === "offered") return <OfferCard card={card} me={me} remaining={0} onActed={onActed} />;
  if (card.heldByLabel) return <CardHistoryTile card={card} onActed={onActed} />;
  // Handed in (submitted) or further along: read-only, no actions - the card is
  // with the client, not the editor's to act on.
  if (!card.uploadOpen && (card.stage === "submitted" || card.stage === "delivered" || card.stage === "handed_over")) return <CardHistoryTile card={card} onActed={onActed} />;
  // in_production/changes_requested are handled by CardDetailsPage before
  // reaching here (spec #316) - this is the fallback for any other stage.
  return <SubmitCard card={card} onActed={onActed} />;
}

function CardHistoryTile({ card, onActed }: { card: EditorCard; onActed: OnActed }) {
  const { t } = useI18n();
  const [newRound, setNewRound] = useState(false);
  const label = t(card.stage === "submitted" ? "dashboard.stageHandedIn" : card.stage === "delivered" ? "dashboard.stageDelivered" : "dashboard.stageHandedOver");
  return (
    <div className="ed-card ed-submit">
      <div className="ed-card-head"><span className="ed-eyebrow">{label}</span></div>
      <h3 className="ed-title">{card.title}</h3>
      <p className="ed-muted" style={{ marginTop: 6 }}>{card.brandName}</p>
      {card.driveNewRound && (newRound ? (
        <DriveHandin workItemId={card.id} title={card.title} onDone={() => onActed()} onCancel={() => setNewRound(false)} primaryClass="ed-primary" buttonClass="ed-secondary" />
      ) : (
        <>
          <p className="ed-muted" style={{ marginTop: 6 }}>{t("dashboard.driveNewRoundNote")}</p>
          <div style={{ marginTop: 12 }}><button type="button" className="ed-primary" onClick={() => setNewRound(true)}>{t("dashboard.driveNewRound")}</button></div>
        </>
      ))}
    </div>
  );
}

function CardUnavailable() {
  const { t } = useI18n();
  return (
    <div className="editor-offer-card editor-offer-empty" data-screen-state="card-unavailable">
      <div className="editor-offer-top">
        <span className="editor-offer-status"><span className="editor-offer-dot muted" />{t("dashboard.cardUnavailable")}</span>
      </div>
      <div className="editor-offer-empty-body">
        <p>{t("dashboard.cardUnavailableBody")}</p>
        <a className="ed-footer-link" href="/editor">{t("dashboard.backToDashboard")}</a>
      </div>
    </div>
  );
}

/**
 * Profile: who the editor is, plus their settings. Billing details is a
 * sub-setting here (Shawn 2026-11-01) rather than its own menu entry.
 */
function EditorProfileScreen({ me, onBack, onBillingDetails }: { me: EditorProfile; onBack: () => void; onBillingDetails: () => void }) {
  const { t } = useI18n();
  return (
    <div className="ed-billing hd-page" data-screen="editor-profile">
      <HubPageHead title={t("dashboard.profileTitle")} backLabel={t("dashboard.profileBack")} onBack={onBack} />
      <div className="ed-profile-id">
        <span className="ed-avatar" aria-hidden="true">{initials(me.displayName)}</span>
        <div>
          <strong>{me.displayName}</strong>
          <p className="ed-muted">{me.email}</p>
        </div>
      </div>
      <button type="button" className="ed-profile-row" onClick={onBillingDetails}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2.5"/><path d="M2 10h20"/></svg>
        <span>
          <strong>{t("dashboard.profileBillingDetails")}</strong>
          <span className="ed-muted">{t("dashboard.profileBillingDetailsHint")}</span>
        </span>
        <svg className="ed-profile-row-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>
      </button>
      <ProfileLocation />
    </div>
  );
}

/** Turn a failed submit into a sentence an editor can act on (spec #145 adds the FreeFrame-specific cases). */
function submitErrorMessage(err: unknown): MessageKey {
  const code = err instanceof ApiError ? err.message : null;
  switch (code) {
    case "no_trello_card":
    case "freeframe_not_configured":
      return "dashboard.submitErrNoFolder";
    case "freeframe_unavailable":
      return "dashboard.submitErrUnavailable";
    case "revision_open":
      return "dashboard.submitErrRevisionOpen";
    case "stage_changed":
      return "dashboard.submitErrStageChanged";
    default:
      return "dashboard.submitErrGeneric";
  }
}

/** The in-production tile: Submit (resolves the FreeFrame destination, then opens it, spec #122/#145) plus Give back (spec #82), the portal-native reversal of accept. */
function SubmitCard({ card, onActed }: { card: EditorCard | undefined; onActed: OnActed }) {
  const i18n = useI18n();
  const { t, eur } = i18n;
  const [busy, setBusy] = useState<"submit" | "give-back" | null>(null);
  const [error, setError] = useState<MessageKey | null>(null);
  const [confirming, setConfirming] = useState(false);
  const uploadUrl = card?.uploadOpen ? card.freeframeUploadUrl : null;
  const [driveOpen, setDriveOpen] = useState(false);

  async function submit() {
    if (!card || busy) return;
    if (card.driveDelivery) {
      setDriveOpen(true);
      return;
    }
    setBusy("submit");
    setError(null);
    // Open the tab from the click itself - a window.open() after an awaited
    // network call is blocked by popup blockers in most browsers - and
    // redirect it once the destination is known. A portal-engine brand's
    // submit resolves the FreeFrame delivery folder server-side and answers
    // with its uploadUrl, bypassing FreeFrame's workspace-picker step
    // entirely (spec #145); every other brand keeps the precomputed
    // handinUrl deep link.
    const tab = window.open("about:blank", "_blank");
    try {
      const result = await post<{ ok: boolean; uploadUrl: string | null }>(`/api/editor/work/${card.id}/submit`);
      const target = result.uploadUrl ?? card.handinUrl ?? HANDIN_URL;
      if (tab) tab.location.href = target;
      else window.open(target, "_blank", "noopener,noreferrer");
    } catch (err) {
      tab?.close();
      setError(submitErrorMessage(err));
    } finally {
      setBusy(null);
      onActed();
    }
  }

  async function giveBack() {
    if (!card || busy) return;
    setBusy("give-back");
    setError(null);
    try {
      // Spec #291: the route answers with the one-line pause+trust-cost
      // summary - hand it up so it survives this tile clearing on reload.
      // The note arrives already in the editor's language (server-side `editor-i18n.ts`, spec #304).
      const result = await post<{ ok: boolean; message?: string }>(`/api/editor/work/${card.id}/give-back`);
      onActed(result.message);
    } catch {
      setError("dashboard.giveBackError");
      onActed();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="ed-card ed-submit">
      <div className="ed-card-head"><span className="ed-eyebrow">{t("dashboard.submitEyebrow")}</span></div>
      {card ? <>
        <h3 className="ed-title">{card.title}</h3>
        <p className="ed-muted" style={{ marginTop: 6 }}>
          {uploadUrl ? t("dashboard.uploadOpen") : t("dashboard.inProduction")}{graceMinutesLeft(card) !== null ? ` · ${t("dashboard.uploadGrace", { minutes: graceMinutesLeft(card)! })}` : card.deadlineAt ? ` · ${t("dashboard.due", { when: relative(card.deadlineAt, t) })}` : ""}
          {card.payoutCents != null ? ` · ${eur(card.payoutCents)}` : ""}
        </p>
        {error && <p className="editor-offer-error">{t(error)}</p>}
        {confirming && card.giveBack ? (
          <GiveBackConfirm
            terms={card.giveBack}
            busy={busy === "give-back"}
            onConfirm={() => void giveBack()}
            onKeep={() => setConfirming(false)}
            buttonClass="ed-secondary"
            primaryClass="ed-primary"
          />
        ) : card.driveDelivery && driveOpen ? (
          <DriveHandin
            workItemId={card.id}
            title={card.title}
            onDone={() => onActed()}
            onCancel={() => setDriveOpen(false)}
            primaryClass="ed-primary"
            buttonClass="ed-secondary"
          />
        ) : (
          <div className="ed-submit-actions">
            {uploadUrl ? (
              <a className="ed-primary" href={uploadUrl} target="_blank" rel="noopener noreferrer">{t("dashboard.uploadOpenLink")}</a>
            ) : (
              <button className="ed-primary" disabled={busy !== null} onClick={() => void submit()}>
                {card.driveDelivery ? t("dashboard.driveSubmit") : busy === "submit" ? t("dashboard.submitting") : t("dashboard.submitForReview")}
              </button>
            )}
            {card.giveBack && (
              <button className="ed-secondary" disabled={busy !== null} onClick={() => setConfirming(true)}>{t("dashboard.giveBack")}</button>
            )}
          </div>
        )}
        {uploadUrl && <p className="ed-muted" style={{ marginTop: 6 }}>{t("dashboard.uploadOpenNote")}</p>}
      </> : <>
        <h3 className="ed-title">{t("dashboard.noCardInProduction")}</h3>
        <p className="ed-muted" style={{ marginTop: 6 }}>{t("dashboard.acceptToStart")}</p>
      </>}
    </div>
  );
}

/**
 * The offer card - the editor's "new card" draw.
 *
 * Visual match to the hub's "Give me a Card" design (orange status dot, round
 * brand avatar, big title, brand subline, meta, script peek, Accept/Decline),
 * but fed by the portal's own dispatch and wired to /api/editor/work. Buttons
 * only, no swipe (Shawn 2026-10-08).
 */
function OfferCard({ card, me, remaining, onActed }: { card: EditorCard; me: EditorProfile; remaining: number; onActed: OnActed }) {
  const i18n = useI18n();
  const { t } = i18n;
  const script = (card.scriptMd ?? "").trim();
  const meta = specSummary(card.deliverableSpec, t);
  const lang = cardLanguage(card, me);
  const earn = earnText(card, i18n);
  return (
    <div className="editor-offer-card">
      <div className="editor-offer-top">
        <span className="editor-offer-status"><span className="editor-offer-dot" />{t("dashboard.offerHereYouGo")}{remaining > 0 ? ` · ${t("dashboard.offerMoreWaiting", { count: remaining })}` : ""}</span>
        {lang && <span className="editor-offer-lang">{lang}</span>}
      </div>
      <div className="editor-offer-identity">
        <span className="editor-offer-avatar" aria-hidden="true">{initials(card.brandName)}</span>
        <div className="editor-offer-copy">
          <h3 className="editor-offer-title">{card.title}</h3>
          <p className="editor-offer-brand">{card.brandName}</p>
        </div>
      </div>
      {(meta || earn || card.offerExpiresAt) && (
        <div className="editor-offer-meta">
          {meta && <span>{meta}</span>}
          {earn && <span>{earn}</span>}
          {card.offerExpiresAt && <span>{t("dashboard.expires", { when: relative(card.offerExpiresAt, t) })}</span>}
        </div>
      )}
      {script && <p className="editor-offer-peek">{script.slice(0, 280)}{script.length > 280 ? "…" : ""}</p>}
      <OfferActions workItemId={card.id} onActed={onActed} />
    </div>
  );
}

/** Accept/decline for one offered card. Disabled mid-flight so a double click can't fire twice. */
function OfferActions({ workItemId, onActed }: { workItemId: string; onActed: OnActed }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [error, setError] = useState<MessageKey | null>(null);

  async function act(action: "accept" | "decline") {
    if (busy) return;
    setBusy(action);
    setError(null);
    try {
      // Spec #291: a decline answers with the pause summary - hand it up since
      // a decline swaps this whole tile out for EmptyOfferCard on reload.
      // The note arrives already in the editor's language (server-side `editor-i18n.ts`, spec #304).
      const result = await post<{ ok: boolean; message?: string }>(`/api/editor/work/${workItemId}/${action}`);
      onActed(result.message);
    } catch {
      setError("dashboard.offerActionError");
      onActed();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="editor-offer-foot">
      {error && <p className="editor-offer-error">{t(error)}</p>}
      <div className="editor-offer-actions">
        <button className="editor-offer-btn primary" disabled={busy !== null} onClick={() => void act("accept")}>
          {busy === "accept" ? t("dashboard.claiming") : t("dashboard.accept")}
        </button>
        <button className="editor-offer-btn" disabled={busy !== null} onClick={() => void act("decline")}>
          {busy === "decline" ? t("dashboard.declining") : t("dashboard.decline")}
        </button>
      </div>
      <p className="editor-offer-note">{t("dashboard.declineNote")}</p>
    </div>
  );
}

/** A compact "40s · 3 hooks · pixar" line from the deliverable spec. */
function specSummary(spec: Record<string, unknown>, t: I18n["t"]): string {
  const parts: string[] = [];
  const n = (k: string) => (typeof spec[k] === "number" ? (spec[k] as number) : undefined);
  const s = (k: string) => (typeof spec[k] === "string" && spec[k] ? (spec[k] as string) : undefined);
  const len = n("length_s"); if (len) parts.push(t("dashboard.specSeconds", { n: len }));
  const hooks = n("hooks"); if (hooks) parts.push(t(hooks === 1 ? "dashboard.specHooksOne" : "dashboard.specHooksMany", { n: hooks }));
  const variants = n("variants"); if (variants && variants > 1) parts.push(t("dashboard.specVariants", { n: variants }));
  const fmt = s("format"); if (fmt) parts.push(fmt);
  const lang = s("language"); if (lang) parts.push(lang.toUpperCase());
  return parts.join(" · ");
}

function SignedOutGate({ returnTo = "/editor" }: { returnTo?: string } = {}) {
  // Editors sign in through central auth (auth.aditor.ai). The button is a plain
  // navigation to the server start route, which 302s to the provider; on return
  // the callback mints the session and lands back on /editor (or the deep link
  // it was opened from, see `returnTo` in EditorScreen).
  const { t } = useI18n();
  const failed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("signin") === "failed";
  function signIn() {
    window.location.href = `/api/auth/oidc/login?returnTo=${returnTo}`;
  }
  return (
    <div className="editor-shell" data-screen="editor-signed-out">
      <div className="editor-gate">
        <h1>{t("dashboard.signInTitle")}</h1>
        <p>{t("dashboard.signInBody")}</p>
        <button className="btn-brand" onClick={signIn}>{t("dashboard.signIn")}</button>
        {failed && <p className="editor-sub" style={{ marginTop: 12 }}>{t("dashboard.signInFailed")}</p>}
      </div>
    </div>
  );
}

function NotEditorGate() {
  const { t } = useI18n();
  async function signOut() {
    try { await fetch("/api/auth/logout", { method: "POST" }); } catch { /* best-effort */ }
    window.location.assign("/");
  }
  return (
    <div className="editor-shell" data-screen="editor-not-editor">
      <div className="editor-gate">
        <h1>{t("dashboard.noAccessTitle")}</h1>
        <p>{t("dashboard.noAccessBody")}</p>
        <button className="editor-muted-link" onClick={signOut}>{t("dashboard.signInOther")}</button>
      </div>
    </div>
  );
}
